import {
  ComponentType,
  createContext,
  ReactNode,
  useContext,
  useLayoutEffect,
  useState,
} from "react";
import { I18nextProvider, useTranslation } from "react-i18next";
import Cookies from "js-cookie";

import _i18n from "../../i18n";

import { LanguageType } from "types";

type TranslationContextValueType = {
  language: LanguageType;
  changeLanguage: (language: LanguageType) => void;
  t: (key: string) => string;
};

interface TranslationContextType {
  children: ReactNode;
}

const TranslationContext = createContext<TranslationContextValueType>(
  {} as TranslationContextValueType
);

const DEFAULT_LANGUAGE: LanguageType = "ja";

const TranslationContextProvider = ({ children }: TranslationContextType) => {
  const [language, setLanguage] = useState<LanguageType>(DEFAULT_LANGUAGE);
  const { t: translate, i18n } = useTranslation();
  const cookieslanguage = Cookies.get("LANGUAGE");

  const changeLanguage = (lang: LanguageType) => {
    Cookies.set("LANGUAGE", lang);
    i18n.changeLanguage(lang);
  };

  const t = (key: string): string => {
    return translate(key);
  };

  useLayoutEffect(() => {
    setLanguage((cookieslanguage as LanguageType) || DEFAULT_LANGUAGE);
    i18n.changeLanguage(cookieslanguage || DEFAULT_LANGUAGE);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cookieslanguage]);

  return (
    <TranslationContext.Provider value={{ language, changeLanguage, t }}>
      <I18nextProvider i18n={_i18n}>{children}</I18nextProvider>
    </TranslationContext.Provider>
  );
};

export default TranslationContextProvider;
export const useTranslationContext = () => useContext(TranslationContext);

export const withTranslationContext = <P extends object>(
  WrappedComponent: ComponentType<P>
) => {
  return (props: P) => {
    const { t } = useTranslationContext();
    return <WrappedComponent {...props} t={t} />;
  };
};
